import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Plus, Save, X, CheckCircle, AlertTriangle, Shield, Globe, Info } from 'lucide-react';

// This is a single textarea component that handles its own state
// to prevent form jumping during typing
const StableTextarea = ({ initialValue, onSave, placeholder, rows = 2 }) => {
  const [value, setValue] = useState(initialValue || '');
  const timeoutRef = useRef(null);
  
  // Update internal state when prop changes (only for external updates)
  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);
  
  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    
    // Debounce the update to parent component
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      onSave(newValue);
    }, 500);
  };
  
  return (
    <textarea
      rows={rows}
      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
    />
  );
};

const PolicyAnalysisEditor = ({ 
  policy, 
  onSave, 
  onCancel, 
  isLoading = false,
  onUpdateTactic = null,
  onDeleteTactic = null,
  onAddTactic = null,
  onUpdateConstitutionalIssue = null,
  onDeleteConstitutionalIssue = null,
  onAddConstitutionalIssue = null,
  onUpdatePrecedent = null,
  onDeletePrecedent = null,
  onAddPrecedent = null,
  onUpdateExcerpt = null,
  onDeleteExcerpt = null,
  onAddExcerpt = null,
}) => {
  const [editedPolicy, setEditedPolicy] = useState({
    score: 0,
    score_tactics: 0,
    score_constitutional: 0, 
    score_precedents: 0,
    tactics: [],
    constitutional_issues: [],
    precedents: [],
    excerpts: [],
  });
  
  const [editSuccess, setEditSuccess] = useState(null);
  const [editError, setEditError] = useState(null);
  
  // severities for selection
  const severities = [
    { value: 1, label: 'Minimal' },
    { value: 2, label: 'Low' },
    { value: 3, label: 'Moderate' },
    { value: 4, label: 'High' },
    { value: 5, label: 'Critical' },
  ];
  
  // Load policy data when component mounts or policy changes
  useEffect(() => {
    if (policy) {
      setEditedPolicy({
        score: policy.score || 0,
        score_tactics: policy.score_tactics || 0,
        score_constitutional: policy.score_constitutional || 0,
        score_precedents: policy.score_precedents || 0,
        tactics: policy.tactics || [],
        constitutional_issues: policy.constitutional_issues || [],
        precedents: policy.precedents || [],
        excerpts: policy.excerpts || [],
      });
    }
  }, [policy]);
  
  // Handle save
  const handleSave = async () => {
    try {
      await onSave({
        score: editedPolicy.score,
        score_tactics: editedPolicy.score_tactics,
        score_constitutional: editedPolicy.score_constitutional,
        score_precedents: editedPolicy.score_precedents,
      });
      
      setEditSuccess('Policy analysis updated successfully');
      setTimeout(() => setEditSuccess(null), 3000);
    } catch (err) {
      setEditError(err.message || 'Failed to update policy analysis');
      setTimeout(() => setEditError(null), 5000);
    }
  };
  
  // Handle updating a tactic field
  const handleUpdateTacticField = (tacticId, field, value) => {
    setEditedPolicy(prevState => ({
      ...prevState,
      tactics: prevState.tactics.map(tactic => 
        tactic.id === tacticId ? { ...tactic, [field]: value } : tactic
      )
    }));
    
    if (onUpdateTactic) {
      const updatedTactic = editedPolicy.tactics.find(t => t.id === tacticId);
      if (updatedTactic) {
        onUpdateTactic({
          ...updatedTactic,
          [field]: value
        });
      }
    }
  };
  
  // Handle deleting a tactic
  const handleDeleteTactic = (tacticId) => {
    if (window.confirm('Are you sure you want to delete this tactic?')) {
      setEditedPolicy(prevState => ({
        ...prevState,
        tactics: prevState.tactics.filter(tactic => tactic.id !== tacticId)
      }));
      
      if (onDeleteTactic) {
        onDeleteTactic(tacticId);
      }
    }
  };
  
  // Handle adding a new tactic
  const handleAddTactic = () => {
    const newTactic = {
      id: `new-tactic-${Date.now()}`, // Temporary ID for UI
      policy_id: policy.id,
      tactic_name: 'New Authoritarian Tactic',
      description: '',
      evidence: '',
      severity: 3,
    };
    
    setEditedPolicy(prevState => ({
      ...prevState,
      tactics: [...prevState.tactics, newTactic]
    }));
    
    if (onAddTactic) {
      onAddTactic(newTactic);
    }
  };
  
  // Handle updating a constitutional issue field
  const handleUpdateConstitutionalField = (issueId, field, value) => {
    setEditedPolicy(prevState => ({
      ...prevState,
      constitutional_issues: prevState.constitutional_issues.map(issue => 
        issue.id === issueId ? { ...issue, [field]: value } : issue
      )
    }));
    
    if (onUpdateConstitutionalIssue) {
      const updatedIssue = editedPolicy.constitutional_issues.find(i => i.id === issueId);
      if (updatedIssue) {
        onUpdateConstitutionalIssue({
          ...updatedIssue,
          [field]: value
        });
      }
    }
  };
  
  // Handle deleting a constitutional issue
  const handleDeleteConstitutionalIssue = (issueId) => {
    if (window.confirm('Are you sure you want to delete this constitutional issue?')) {
      setEditedPolicy(prevState => ({
        ...prevState,
        constitutional_issues: prevState.constitutional_issues.filter(issue => issue.id !== issueId)
      }));
      
      if (onDeleteConstitutionalIssue) {
        onDeleteConstitutionalIssue(issueId);
      }
    }
  };
  
  // Handle adding a new constitutional issue
  const handleAddConstitutionalIssue = () => {
    const newIssue = {
      id: `new-constitutional-${Date.now()}`, // Temporary ID for UI
      policy_id: policy.id,
      issue_name: 'New Constitutional Issue',
      description: '',
      evidence: '',
      severity: 3,
    };
    
    setEditedPolicy(prevState => ({
      ...prevState,
      constitutional_issues: [...prevState.constitutional_issues, newIssue]
    }));
    
    if (onAddConstitutionalIssue) {
      onAddConstitutionalIssue(newIssue);
    }
  };
  
  // Handle updating a precedent field
  const handleUpdatePrecedentField = (precedentId, field, value) => {
    setEditedPolicy(prevState => ({
      ...prevState,
      precedents: prevState.precedents.map(precedent => 
        precedent.id === precedentId ? { ...precedent, [field]: value } : precedent
      )
    }));
    
    if (onUpdatePrecedent) {
      const updatedPrecedent = editedPolicy.precedents.find(p => p.id === precedentId);
      if (updatedPrecedent) {
        onUpdatePrecedent({
          ...updatedPrecedent,
          [field]: value
        });
      }
    }
  };
  
  // Handle deleting a precedent
  const handleDeletePrecedent = (precedentId) => {
    if (window.confirm('Are you sure you want to delete this precedent?')) {
      setEditedPolicy(prevState => ({
        ...prevState,
        precedents: prevState.precedents.filter(precedent => precedent.id !== precedentId)
      }));
      
      if (onDeletePrecedent) {
        onDeletePrecedent(precedentId);
      }
    }
  };
  
  // Handle adding a new precedent
  const handleAddPrecedent = () => {
    const newPrecedent = {
      id: `new-precedent-${Date.now()}`, // Temporary ID for UI
      policy_id: policy.id,
      precedent_name: 'New Legal Precedent',
      description: '',
      relevance: '',
      severity: 3,
    };
    
    setEditedPolicy(prevState => ({
      ...prevState,
      precedents: [...prevState.precedents, newPrecedent]
    }));
    
    if (onAddPrecedent) {
      onAddPrecedent(newPrecedent);
    }
  };
  
  // Handle updating an excerpt field
  const handleUpdateExcerptField = (excerptId, field, value) => {
    setEditedPolicy(prevState => ({
      ...prevState,
      excerpts: prevState.excerpts.map(excerpt => 
        excerpt.id === excerptId ? { ...excerpt, [field]: value } : excerpt
      )
    }));
    
    if (onUpdateExcerpt) {
      const updatedExcerpt = editedPolicy.excerpts.find(e => e.id === excerptId);
      if (updatedExcerpt) {
        onUpdateExcerpt({
          ...updatedExcerpt,
          [field]: value
        });
      }
    }
  };
  
  // Handle deleting an excerpt
  const handleDeleteExcerpt = (excerptId) => {
    if (window.confirm('Are you sure you want to delete this excerpt?')) {
      setEditedPolicy(prevState => ({
        ...prevState,
        excerpts: prevState.excerpts.filter(excerpt => excerpt.id !== excerptId)
      }));
      
      if (onDeleteExcerpt) {
        onDeleteExcerpt(excerptId);
      }
    }
  };
  
  // Handle adding a new excerpt
  const handleAddExcerpt = () => {
    const newExcerpt = {
      id: `new-excerpt-${Date.now()}`, // Temporary ID for UI
      policy_id: policy.id,
      excerpt_text: 'New Policy Excerpt',
      context: '',
      importance: 3,
    };
    
    setEditedPolicy(prevState => ({
      ...prevState,
      excerpts: [...prevState.excerpts, newExcerpt]
    }));
    
    if (onAddExcerpt) {
      onAddExcerpt(newExcerpt);
    }
  };
  
  if (!policy) {
    return (
      <div className="bg-gray-50 p-4 rounded-md">
        <p className="text-gray-500">No policy selected</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Success/Error messages */}
      {editSuccess && (
        <div className="p-4 bg-green-50 border-l-4 border-green-500 text-green-700">
          <div className="flex">
            <CheckCircle className="h-5 w-5 mr-2" />
            <span>{editSuccess}</span>
          </div>
        </div>
      )}
      
      {editError && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>{editError}</span>
          </div>
        </div>
      )}
      
      {/* Score section */}
      <div className="bg-gray-50 p-4 rounded-md">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Threat Scores</h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="score" className="block text-sm font-medium text-gray-700">
              Overall Threat Score (0-100)
            </label>
            <input
              type="number"
              id="score"
              min="0"
              max="100"
              value={editedPolicy.score}
              onChange={(e) => setEditedPolicy(prev => ({...prev, score: parseInt(e.target.value) || 0}))}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label htmlFor="score_tactics" className="block text-sm font-medium text-gray-700">
              Authoritarian Tactics Score (0-100)
            </label>
            <input
              type="number"
              id="score_tactics"
              min="0"
              max="100"
              value={editedPolicy.score_tactics}
              onChange={(e) => setEditedPolicy(prev => ({...prev, score_tactics: parseInt(e.target.value) || 0}))}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label htmlFor="score_constitutional" className="block text-sm font-medium text-gray-700">
              Constitutional Issues Score (0-100)
            </label>
            <input
              type="number"
              id="score_constitutional"
              min="0"
              max="100"
              value={editedPolicy.score_constitutional}
              onChange={(e) => setEditedPolicy(prev => ({...prev, score_constitutional: parseInt(e.target.value) || 0}))}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label htmlFor="score_precedents" className="block text-sm font-medium text-gray-700">
              Legal Precedents Score (0-100)
            </label>
            <input
              type="number"
              id="score_precedents"
              min="0"
              max="100"
              value={editedPolicy.score_precedents}
              onChange={(e) => setEditedPolicy(prev => ({...prev, score_precedents: parseInt(e.target.value) || 0}))}
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>
      </div>
      
      {/* Authoritarian Tactics section */}
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Authoritarian Tactics</h2>
          <button
            type="button"
            onClick={handleAddTactic}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Tactic
          </button>
        </div>
        
        <div className="space-y-4">
          {editedPolicy.tactics.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No tactics defined</p>
          ) : (
            editedPolicy.tactics.map((tactic, index) => (
              <div key={tactic.id} className="border rounded-md p-4 bg-white">
                <div className="flex justify-between items-start mb-2">
                  <div className="w-full">
                    <div className="mb-2">
                      <label htmlFor={`tactic-${tactic.id}-name`} className="block text-sm font-medium text-gray-700">
                        Tactic Name
                      </label>
                      <input
                        type="text"
                        id={`tactic-${tactic.id}-name`}
                        value={tactic.tactic_name || ''}
                        onChange={(e) => handleUpdateTacticField(tactic.id, 'tactic_name', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="mb-2">
                      <label htmlFor={`tactic-${tactic.id}-description`} className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        id={`tactic-${tactic.id}-description`}
                        value={tactic.description || ''}
                        onChange={(e) => handleUpdateTacticField(tactic.id, 'description', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="mb-2">
                      <label htmlFor={`tactic-${tactic.id}-evidence`} className="block text-sm font-medium text-gray-700">
                        Evidence
                      </label>
                      <textarea
                        id={`tactic-${tactic.id}-evidence`}
                        value={tactic.evidence || ''}
                        onChange={(e) => handleUpdateTacticField(tactic.id, 'evidence', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor={`tactic-${tactic.id}-severity`} className="block text-sm font-medium text-gray-700">
                        Severity
                      </label>
                      <select
                        id={`tactic-${tactic.id}-severity`}
                        value={tactic.severity || 3}
                        onChange={(e) => handleUpdateTacticField(tactic.id, 'severity', parseInt(e.target.value))}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        {severities.map(severity => (
                          <option key={severity.value} value={severity.value}>{severity.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => handleDeleteTactic(tactic.id)}
                    className="ml-4 p-1 text-red-600 hover:bg-red-50 rounded-full"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Constitutional Issues section */}
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Constitutional Issues</h2>
          <button
            type="button"
            onClick={handleAddConstitutionalIssue}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Issue
          </button>
        </div>
        
        <div className="space-y-4">
          {editedPolicy.constitutional_issues.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No constitutional issues defined</p>
          ) : (
            editedPolicy.constitutional_issues.map((issue, index) => (
              <div key={issue.id} className="border rounded-md p-4 bg-white">
                <div className="flex justify-between items-start mb-2">
                  <div className="w-full">
                    <div className="mb-2">
                      <label htmlFor={`issue-${issue.id}-name`} className="block text-sm font-medium text-gray-700">
                        Issue Name
                      </label>
                      <input
                        type="text"
                        id={`issue-${issue.id}-name`}
                        value={issue.issue_name || ''}
                        onChange={(e) => handleUpdateConstitutionalField(issue.id, 'issue_name', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="mb-2">
                      <label htmlFor={`issue-${issue.id}-description`} className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        id={`issue-${issue.id}-description`}
                        value={issue.description || ''}
                        onChange={(e) => handleUpdateConstitutionalField(issue.id, 'description', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="mb-2">
                      <label htmlFor={`issue-${issue.id}-evidence`} className="block text-sm font-medium text-gray-700">
                        Evidence
                      </label>
                      <textarea
                        id={`issue-${issue.id}-evidence`}
                        value={issue.evidence || ''}
                        onChange={(e) => handleUpdateConstitutionalField(issue.id, 'evidence', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor={`issue-${issue.id}-severity`} className="block text-sm font-medium text-gray-700">
                        Severity
                      </label>
                      <select
                        id={`issue-${issue.id}-severity`}
                        value={issue.severity || 3}
                        onChange={(e) => handleUpdateConstitutionalField(issue.id, 'severity', parseInt(e.target.value))}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        {severities.map(severity => (
                          <option key={severity.value} value={severity.value}>{severity.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => handleDeleteConstitutionalIssue(issue.id)}
                    className="ml-4 p-1 text-red-600 hover:bg-red-50 rounded-full"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Legal Precedents section */}
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Legal Precedents</h2>
          <button
            type="button"
            onClick={handleAddPrecedent}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Precedent
          </button>
        </div>
        
        <div className="space-y-4">
          {editedPolicy.precedents.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No legal precedents defined</p>
          ) : (
            editedPolicy.precedents.map((precedent, index) => (
              <div key={precedent.id} className="border rounded-md p-4 bg-white">
                <div className="flex justify-between items-start mb-2">
                  <div className="w-full">
                    <div className="mb-2">
                      <label htmlFor={`precedent-${precedent.id}-name`} className="block text-sm font-medium text-gray-700">
                        Precedent Name
                      </label>
                      <input
                        type="text"
                        id={`precedent-${precedent.id}-name`}
                        value={precedent.precedent_name || ''}
                        onChange={(e) => handleUpdatePrecedentField(precedent.id, 'precedent_name', e.target.value)}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="mb-2">
                      <label htmlFor={`precedent-${precedent.id}-description`} className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        id={`precedent-${precedent.id}-description`}
                        value={precedent.description || ''}
                        onChange={(e) => handleUpdatePrecedentField(precedent.id, 'description', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="mb-2">
                      <label htmlFor={`precedent-${precedent.id}-relevance`} className="block text-sm font-medium text-gray-700">
                        Relevance
                      </label>
                      <textarea
                        id={`precedent-${precedent.id}-relevance`}
                        value={precedent.relevance || ''}
                        onChange={(e) => handleUpdatePrecedentField(precedent.id, 'relevance', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor={`precedent-${precedent.id}-severity`} className="block text-sm font-medium text-gray-700">
                        Relevance Severity
                      </label>
                      <select
                        id={`precedent-${precedent.id}-severity`}
                        value={precedent.severity || 3}
                        onChange={(e) => handleUpdatePrecedentField(precedent.id, 'severity', parseInt(e.target.value))}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        {severities.map(severity => (
                          <option key={severity.value} value={severity.value}>{severity.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => handleDeletePrecedent(precedent.id)}
                    className="ml-4 p-1 text-red-600 hover:bg-red-50 rounded-full"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Key Excerpts section */}
      <div className="bg-gray-50 p-4 rounded-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Key Policy Excerpts</h2>
          <button
            type="button"
            onClick={handleAddExcerpt}
            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Excerpt
          </button>
        </div>
        
        <div className="space-y-4">
          {editedPolicy.excerpts.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No key excerpts defined</p>
          ) : (
            editedPolicy.excerpts.map((excerpt, index) => (
              <div key={excerpt.id} className="border rounded-md p-4 bg-white">
                <div className="flex justify-between items-start mb-2">
                  <div className="w-full">
                    <div className="mb-2">
                      <label htmlFor={`excerpt-${excerpt.id}-text`} className="block text-sm font-medium text-gray-700">
                        Excerpt Text
                      </label>
                      <textarea
                        id={`excerpt-${excerpt.id}-text`}
                        value={excerpt.excerpt_text || ''}
                        onChange={(e) => handleUpdateExcerptField(excerpt.id, 'excerpt_text', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div className="mb-2">
                      <label htmlFor={`excerpt-${excerpt.id}-context`} className="block text-sm font-medium text-gray-700">
                        Context
                      </label>
                      <textarea
                        id={`excerpt-${excerpt.id}-context`}
                        value={excerpt.context || ''}
                        onChange={(e) => handleUpdateExcerptField(excerpt.id, 'context', e.target.value)}
                        rows={3}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor={`excerpt-${excerpt.id}-importance`} className="block text-sm font-medium text-gray-700">
                        Significance
                      </label>
                      <select
                        id={`excerpt-${excerpt.id}-importance`}
                        value={excerpt.importance || 3}
                        onChange={(e) => handleUpdateExcerptField(excerpt.id, 'importance', parseInt(e.target.value))}
                        className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        {severities.map(severity => (
                          <option key={severity.value} value={severity.value}>{severity.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => handleDeleteExcerpt(excerpt.id)}
                    className="ml-4 p-1 text-red-600 hover:bg-red-50 rounded-full"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Action buttons */}
      <div className="flex justify-end pt-5">
        <button
          type="button"
          onClick={onCancel}
          className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading}
          className="ml-3 inline-flex items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-1" />
              Save Analysis
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default PolicyAnalysisEditor;